import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '@/utils/axiosInstance';

// Global cache for reseller images (persists across component mounts)
let globalResellerImagesCache = null;
let globalCachePromise = null;

/**
 * Hook to get reseller images from the database
 * Images are cached globally to avoid multiple API calls
 * @returns {{ getResellerImage: (domain: string) => string | null, loading: boolean, resellerImagesMap: Object }}
 */
export function useResellerImages() {
  const [resellerImagesMap, setResellerImagesMap] = useState(globalResellerImagesCache || {});
  const [loading, setLoading] = useState(!globalResellerImagesCache);

  useEffect(() => {
    // If already cached, use it
    if (globalResellerImagesCache) {
      setResellerImagesMap(globalResellerImagesCache);
      setLoading(false);
      return;
    }

    // If a fetch is already in progress, wait for it
    if (globalCachePromise) {
      globalCachePromise.then((map) => {
        setResellerImagesMap(map);
        setLoading(false);
      });
      return;
    }

    // Fetch resellers from API
    globalCachePromise = axiosInstance.get('/api/resellers/available/')
      .then((response) => {
        const resellers = response.data.resellers || response.data || [];
        const map = {};

        resellers.forEach((reseller) => {
          if (reseller.image) {
            // Use reseller name as key (e.g., 'nedgis', 'andlight')
            const key = reseller.reseller.toLowerCase();
            map[key] = reseller.image;
          }
        });

        globalResellerImagesCache = map;
        return map;
      })
      .catch((error) => {
        console.error('Failed to load reseller images:', error);
        globalResellerImagesCache = {};
        return {};
      })
      .finally(() => {
        globalCachePromise = null;
      });

    globalCachePromise.then((map) => {
      setResellerImagesMap(map);
      setLoading(false);
    });
  }, []);

  /**
   * Get the image URL for a reseller domain
   * @param {string} domain - Full domain (e.g., 'www.nedgis.com') or short domain (e.g., 'nedgis')
   * @returns {string | null} - Image URL or null if not found
   */
  const getResellerImage = useCallback((domain) => {
    if (!domain) return null;

    // Extract short domain (e.g., 'nedgis' from 'www.nedgis.com')
    const parts = domain.split('.');
    const shortDomain = parts.length >= 2
      ? parts[parts.length - 2].toLowerCase()
      : domain.toLowerCase();

    return resellerImagesMap[shortDomain] || null;
  }, [resellerImagesMap]);

  return { getResellerImage, loading, resellerImagesMap };
}

/**
 * Clear the global cache (useful for testing or forced refresh)
 */
export function clearResellerImagesCache() {
  globalResellerImagesCache = null;
  globalCachePromise = null;
}

export default useResellerImages;
